import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { IconOnaCopy } from "@/assets/icons/IconOnaCopy";
import { IconOnaDownload } from "@/assets/icons/IconOnaDownload";
import { IconOnaFolder } from "@/assets/icons/IconOnaFolder";
import { IconOnaReset } from "@/assets/icons/IconOnaReset";
import { DeleteAgentExecutionModal } from "@/components/ai/DeleteAgentExecutionModal";
import { OverrideConversationUrlModal } from "@/components/ai/OverrideConversationUrlModal";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { agentExecutionName } from "@/utils/agent-executions";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export const AgentExecutionDropdownMenu: FC<{
    agentExecution: PlainAgentExecution;
}> = ({ agentExecution }) => {
    const [showDeleteAgentExecutionModal, setShowDeleteAgentExecutionModal] = useState(false);
    const [showOverrideUrlModal, setShowOverrideUrlModal] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const { copyAgentExecutionId, downloadSupportBundle, isSupportBundleAvailable } =
        useAgentExecutionActions(agentExecution);

    const navigate = useNavigate();

    const environmentId = useMemo(() => {
        const usedEnvironments = agentExecution.status?.usedEnvironments;
        if (!usedEnvironments || usedEnvironments.length === 0) {
            return undefined;
        }
        return usedEnvironments[0]?.environmentId;
    }, [agentExecution.status?.usedEnvironments]);

    const { data: environment } = useEnvironment(environmentId);

    const handleOpenProjectSettings = () => {
        const projectId = environment?.metadata?.projectId;
        if (projectId) {
            navigate(`/projects/${projectId}/settings`);
        }
    };

    const canOpenProjectSettings = !!environment?.metadata?.projectId;
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    const getSupportBundleTooltipMessage = () => {
        const availableWhenRunningEnvMsg = "Available on running environments only";
        const tempUnavailableMsg = "Support bundle is temporarily unavailable";

        if (isSupportBundleAvailable) {
            return undefined;
        }

        if (!environment) {
            return tempUnavailableMsg;
        }

        const phase = environment.status?.phase;
        switch (phase) {
            case EnvironmentPhase.RUNNING:
                return undefined;
            case EnvironmentPhase.UPDATING:
            case EnvironmentPhase.CREATING:
            case EnvironmentPhase.STARTING:
                return tempUnavailableMsg;
            default:
                return availableWhenRunningEnvMsg;
        }
    };

    return (
        <>
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger
                    data-testid="agent-execution-options-trigger"
                    className="group flex min-w-0 cursor-pointer items-center focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-0 focus-visible:outline-content-primary"
                    aria-label="More actions"
                >
                    <div
                        className={cn(
                            "flex min-w-0 items-center gap-1 rounded-l-md p-1 transition-colors group-hover:bg-surface-hover group-data-[state=open]:bg-surface-hover",
                        )}
                    >
                        <span className="min-w-0 truncate text-left text-base font-bold">
                            {agentExecutionName(agentExecution)}
                        </span>
                    </div>
                    <div
                        className={cn(
                            "flex flex-shrink-0 items-center rounded-r-md px-1 py-1.5 transition-colors group-hover:bg-surface-hover group-data-[state=open]:bg-surface-hover",
                        )}
                    >
                        <IconChevronDownSmall size="sm" className="size-4" />
                    </div>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-56">
                    <Tooltip
                        content={!canOpenProjectSettings ? "No project associated with this environment" : undefined}
                    >
                        <div>
                            <DropdownMenuItem
                                data-testid="menu-item-open-project-settings"
                                onClick={handleOpenProjectSettings}
                                disabled={!canOpenProjectSettings}
                                LeadingIcon={IconOnaFolder}
                                data-tracking-id="go-to-project"
                            >
                                Go to project
                            </DropdownMenuItem>
                        </div>
                    </Tooltip>

                    <DropdownMenuItem
                        data-testid="menu-item-copy-agent-execution-id"
                        onClick={copyAgentExecutionId}
                        LeadingIcon={IconOnaCopy}
                        data-tracking-id="copy-session-id"
                    >
                        Copy session ID
                    </DropdownMenuItem>

                    <Tooltip content={getSupportBundleTooltipMessage()}>
                        <div>
                            <DropdownMenuItem
                                data-testid="menu-item-download-support-bundle"
                                onClick={downloadSupportBundle}
                                disabled={!isSupportBundleAvailable}
                                LeadingIcon={IconOnaDownload}
                                data-tracking-id="download-support-bundle"
                            >
                                Download support bundle
                            </DropdownMenuItem>
                        </div>
                    </Tooltip>

                    <DropdownMenuItem
                        data-testid="menu-item-delete-agent-execution"
                        onClick={() => setShowDeleteAgentExecutionModal(true)}
                        variant="destructive"
                        LeadingIcon={IconOnaReset}
                        data-tracking-id="reset-session"
                    >
                        Reset session
                    </DropdownMenuItem>
                    {isLocalhost && (
                        <>
                            <DropdownMenuLabel>Development</DropdownMenuLabel>
                            <DropdownMenuItem
                                data-testid="menu-item-override-conversation-url"
                                onClick={() => setShowOverrideUrlModal(true)}
                                data-tracking-id="override-conversation-url"
                            >
                                Override conversation URL
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <DeleteAgentExecutionModal
                agentExecution={agentExecution}
                open={showDeleteAgentExecutionModal}
                onClose={() => setShowDeleteAgentExecutionModal(false)}
            />

            <OverrideConversationUrlModal
                agentExecution={agentExecution}
                open={showOverrideUrlModal}
                onClose={() => setShowOverrideUrlModal(false)}
            />
        </>
    );
};
